import { useEffect, useMemo } from 'react';
import { api } from '../api-client.config';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PuzzleItem } from '../types';

interface UseGetPuzzleItemsQueryOptions {
  limit?: number;
  languageCode?: string;
}

const getPuzzleItems = (params: {
  offset: number;
  limit: number;
  languageCode: string;
}): Promise<{ puzzleItems: PuzzleItem[] }> =>
  api.get(`/puzzle-items`, { params });

export const useGetPuzzleItemsQuery = (
  options?: UseGetPuzzleItemsQueryOptions
) => {
  const { limit = 20, languageCode = '' } = options || {};

  return useInfiniteQuery({
    queryKey: ['puzzle-items', languageCode],
    queryFn: ({ pageParam }) =>
      getPuzzleItems({ offset: pageParam * limit, limit, languageCode }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.puzzleItems?.length < limit
        ? undefined
        : allPages?.length;
    },
    initialPageParam: 0,
    select: (data) => {
      // Merge leaderboard data from each page into a single array.
      // Keep all other properties as they are on page 0.
      if (!data?.pages?.length) return null;
      const mergedPuzzleItems = data.pages.flatMap(
        (page) => page.puzzleItems || []
      );
      return mergedPuzzleItems;
    },
  });
};

export const useGetAllPuzzleItemsQuery = (
  options?: UseGetPuzzleItemsQueryOptions
) => {
  const { limit = 100 } = options || {};

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['all-puzzle-items'],
    queryFn: ({ pageParam }) =>
      getPuzzleItems({ offset: pageParam * limit, limit }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.puzzleItems?.length < limit
        ? undefined
        : allPages?.length;
    },
    initialPageParam: 0,
  });

  // Automatically fetch all pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Merge all pages into a single array
  const puzzleItems = useMemo(() => {
    if (!data?.pages?.length) return [];
    return data.pages.flatMap((page) => page.puzzleItems || []);
  }, [data]);

  return {
    data: puzzleItems,
    isLoading: isLoading || (isFetching && puzzleItems.length === 0),
    isFetching,
    error,
  };
};
